import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text } from '@atlaskit/primitives';
import Link from '@atlaskit/link';

import { ProfileCardWrapper } from '@confluence/profile';
import type { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { PEOPLE_DIRECTORY } from '@confluence/named-routes';

import {
	ContentOwnershipContextProvider,
	ContentOwnershipContextConsumer,
} from '../ContentOwnershipContext';
import type { ContentOwnershipContextType } from '../ContentOwnershipContext';

import { ChangeContentOwnerButton } from './common/ChangeContentOwnerButton';
import { useIsChangeOwnerAvailable } from './common/useIsChangeOwnerAvailable';
import { useUserTitle } from './common/useUserTitle';
import { getChangeOwnerButtonAnalyticsContext } from './common/getChangeOwnerButtonAnalyticsContext';

const i18n = defineMessages({
	owner: {
		id: 'content-ownership.content-owner-item.owner',
		defaultMessage: 'Owner',
		description: 'A label distinguishing the user as the owner of a page',
	},
	creator: {
		id: 'content-ownership.content-owner-item.creator',
		defaultMessage: 'Creator',
		description: 'A label distinguishing the user as the person who created the blogpost',
	},
	ownerOrCreatorWithContributors: {
		id: 'content-ownership.content-owner-item.owner-or-creator-with-contributors',
		defaultMessage:
			'{isBlogpost, select, true {Creator} other {Owner}}{noOtherContributors, select, true {, no contributors} other {}}',
		description:
			'A label showing whether the user is an owner or creator, and if there are other contributors',
	},
	changeOwner: {
		id: 'content-ownership.content-owner-item.change-owner',
		defaultMessage: 'Change owner',
		description: 'An icon label within a button used to change the owner of a page',
	},
	accessibleAvatarLabel: {
		id: 'content-ownership.content-owner-item.accessible-avatar-label',
		defaultMessage: '{displayName}’s profile picture',
		description: "Alt text and aria-label for a user's profile picture",
	},
});

type ContentOwnerItemForDetailsPanelProps = {
	ownerAccountId?: string | null;
	ownerName?: string | null;
	ownerAvatarUrl?: string | null;
	contentType?: string | null;
	contentStatus?: ContentStatus | string | null;
	noOtherContributors?: boolean;
	isCurrentUserSpaceAdmin: boolean;
	analyticsSource: string;
};

function noop() {
	// intentionally empty
}

export const ContentOwnerItemForDetailsPanel: FC<ContentOwnerItemForDetailsPanelProps> = ({
	ownerAccountId,
	ownerName,
	ownerAvatarUrl,
	contentType,
	contentStatus,
	noOtherContributors,
	isCurrentUserSpaceAdmin,
	analyticsSource,
}) => {
	const { formatMessage } = useIntl();

	const ownerProfileUrl = ownerAccountId ? `${PEOPLE_DIRECTORY.toUrl()}/${ownerAccountId}` : '';

	const avatarLabel = formatMessage(i18n.accessibleAvatarLabel, {
		displayName: ownerName,
	});
	const userTitle = useUserTitle({
		contentType,
		noOtherContributors,
	});
	const isChangeOwnerAvailable = useIsChangeOwnerAvailable({
		ownerAccountId,
		isCurrentUserSpaceAdmin,
		contentType,
		contentStatus,
	});

	return (
		<Inline spread="space-between" alignBlock="center" space="space.100">
			<Inline alignBlock="center" space="space.100">
				<ProfileCardWrapper
					userId={ownerAccountId}
					position="left"
					trigger="hover"
					fullName={ownerName ?? undefined}
				>
					<Avatar
						size="large"
						borderColor="transparent"
						href={ownerProfileUrl}
						src={ownerAvatarUrl ?? undefined}
						name={avatarLabel}
						onClick={
							/* an analytics event is only fired if onClick is provided,
                            but providing onClick will change behavior if href is not provided */
							ownerProfileUrl ? noop : undefined
						}
						analyticsContext={{
							source: analyticsSource,
							attributes: { role: 'owner', name: 'profileImage' },
						}}
					/>
				</ProfileCardWrapper>
				<Box>
					<Box>
						<Link
							appearance="subtle"
							href={ownerProfileUrl}
							analyticsContext={{
								source: analyticsSource,
								attributes: { role: 'owner', name: 'profileName' },
							}}
						>
							{ownerName}
						</Link>
					</Box>
					<Inline>
						<Text size="small" color="color.text.subtlest">
							{userTitle}
						</Text>
					</Inline>
				</Box>
			</Inline>
			{isChangeOwnerAvailable && (
				<ContentOwnershipContextProvider>
					<ContentOwnershipContextConsumer>
						{({ openModal }: ContentOwnershipContextType) => {
							return (
								<ChangeContentOwnerButton
									onClick={openModal}
									analyticsContext={getChangeOwnerButtonAnalyticsContext(analyticsSource, 'false')}
								/>
							);
						}}
					</ContentOwnershipContextConsumer>
				</ContentOwnershipContextProvider>
			)}
		</Inline>
	);
};
